import {
  Alert,
  Box,
  Button,
  Card,
  Text,
  Title,
  Link,
  Input,
  Tag,
  Thumbnail,
} from '@nimbus-ds/components';
import { PlanDisplay } from '@nimbus-ds/patterns';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { TBillingStatus } from '../../types/index';
import { formatDate, formatMoney } from '../../utils/formatUtils';
import SubmitButton from '../SubmitButton';
import { CalloutCard } from '@nimbus-ds/patterns';
import { PesoIcon, CheckIcon } from '@nimbus-ds/icons';
import { useApiClients } from '../../context';

// Tipos para os planos da Stripe
export interface StripePrice {
  stripe_price_id: string;
  amount: number;
  currency: string;
  interval: 'month' | 'year';
  intervalCount: 1 | 3 | 6 | 12;
  nickname: string;
  metadata: string[];
  isDefault: boolean;
}

export interface StripePlansResponse {
  stripe_product_id: string;
  name: string;
  prices: StripePrice[];
}

const BillingInfo = () => {
  const { t } = useTranslation();

  const [subscription, setSubscription] = useState<TBillingStatus>();
  const [loading, setLoading] = useState(true);
  const [ctaLoading, setCtaLoading] = useState(false);
  const [coupons, setCoupons] = useState<Record<string, string>>({});
  const [plans, setPlans] = useState<StripePlansResponse | null>(null);
  const [plansLoading, setPlansLoading] = useState(false);

  const { HuappsApi } = useApiClients();

  const getSubscription = async () => {
    const { data } = await HuappsApi.get<TBillingStatus>(
      '/billing/subscription',
    );
    if (data) setSubscription(data);
    setLoading(false);
  };

  const getPlans = async () => {
    try {
      setPlansLoading(true);
      const { data } =
        await HuappsApi.get<StripePlansResponse>('/billing/plans');
      if (data) setPlans(data);
    } catch (error) {
      console.error('Erro ao buscar planos:', error);
    } finally {
      setPlansLoading(false);
    }
  };

  const createCheckoutSession = async (
    priceId?: string,
    couponCode?: string,
  ) => {
    try {
      setCtaLoading(true);
      const { data } = await HuappsApi.post<{ sessionUrl: string }>(
        '/billing/checkout-session',
        {
          coupon: couponCode || '',
          price_id: priceId,
        },
      );
      if (data?.sessionUrl) {
        window.parent.location.href = data.sessionUrl;
        return;
      }

      throw Error('Erro ao criar sessão de checkout');
    } catch (error) {
      console.warn(error);
      setCtaLoading(false);
    }
  };

  useEffect(() => {
    getSubscription();
  }, []);

  // Buscar planos quando não há assinatura ativa
  useEffect(() => {
    if (subscription && subscription.status !== 'active') {
      getPlans();
    }
  }, [subscription]);

  const renderStatus = () => {
    switch (subscription?.status) {
      case 'active':
        return (
          <Alert
            appearance="success"
            title={t('BillingInfoComponent.StatusActiveTitle') || ''}
          >
            <Text>
              {t('BillingInfoComponent.StatusActiveNextBill', {
                date: new Date(
                  subscription.currentPeriodEnd!,
                ).toLocaleDateString(),
              })}
            </Text>
          </Alert>
        );
      case 'trialing':
        return (
          <Alert
            appearance="neutral"
            title={t('BillingInfoComponent.StatusTrialingTitle') || ''}
          >
            <Text>
              {t('BillingInfoComponent.StatusTrialingUntil', {
                date: new Date(
                  subscription.currentPeriodEnd!,
                ).toLocaleDateString(),
              })}
            </Text>
          </Alert>
        );
      case 'past_due':
        return (
          <Alert
            appearance="warning"
            title={t('BillingInfoComponent.StatusPastDueTitle') || ''}
          >
            <Text>{t('BillingInfoComponent.StatusPastDueMessage')}</Text>
          </Alert>
        );
      case 'canceled':
      case 'incomplete':
      case 'unpaid':
        return (
          <Alert
            appearance="danger"
            title={t('BillingInfoComponent.StatusCanceledTitle') || ''}
          >
            <Text>{t('BillingInfoComponent.StatusCanceledMessage')}</Text>
          </Alert>
        );
      default:
        return null;
    }
  };

  return (
    <Box display="grid" gap="6">
      <Title as="h3">{t('BillingInfoComponent.Title')}</Title>

      {/* Botão para assinatura ativa */}

      {loading ? (
        <Card>
          <Card.Body>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap="6"
            >
              <Box display="flex" gap="2" alignItems="center">
                <Thumbnail.Skeleton width="72px" />
                <Box display="grid" gap="2">
                  <Title.Skeleton height="16px" />
                  <Text.Skeleton width="100%" />
                </Box>
              </Box>
              <Button.Skeleton />
            </Box>
          </Card.Body>
        </Card>
      ) : (
        subscription?.status === 'active' && (
          <Card>
            <Card.Body>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap="6"
              >
                <Box display="flex" gap="2" alignItems="center">
                  <Thumbnail
                    width="72px"
                    src={subscription.thumbnailUrl || undefined}
                    alt={subscription.appName}
                  />
                  <Box display="grid" gap="2">
                    <Title as="h5">{subscription.appName}</Title>
                    <Text>
                      {t('BillingInfo.Plan')}:{' '}
                      <Text fontWeight="bold">
                        {formatMoney(
                          subscription.plan?.amount || 0,
                          subscription.plan?.currency || 'BRL',
                          undefined,
                          { display: 'code' },
                        )}
                        /{subscription.plan?.name}
                      </Text>
                    </Text>
                  </Box>
                </Box>
                <SubmitButton
                  loading={ctaLoading}
                  loadingText={t('BillingInfoComponent.Manage') || ''}
                  appearance="primary"
                  onClick={() => createCheckoutSession()}
                >
                  {t('BillingInfoComponent.Manage') || ''}
                </SubmitButton>
              </Box>
            </Card.Body>
          </Card>
        )
      )}

      {loading ? <Alert.Skeleton /> : renderStatus()}

      {!loading && subscription?.invoices?.[0]?.date && (
        <CalloutCard
          appearance="primary"
          title={t('BillingInfoComponent.LastInvoice')}
          subtitle={`${t(
            `BillingStatus.${subscription.invoices[0].status}` || '',
          )} - ${formatDate(subscription.invoices[0].date)}`}
          icon={PesoIcon}
          link={
            <Link
              appearance="primary"
              as="a"
              href={subscription?.latestInvoiceUrl ?? '#'}
              target="_blank"
              rel="noreferrer"
            >
              {t('BillingInfoComponent.ViewInvoice') || ''}
            </Link>
          }
        />
      )}

      {!loading && subscription?.status !== 'active' && (
        <Card>
          <Card.Body>
            <Box>
              <Box display="grid" gap="6">
                <Box>
                  <Title textAlign="center" as="h6">
                    {subscription?.appName}
                  </Title>
                  <Title textAlign="center" as="h3">
                    {t('BillingInfo.PlansTitle') || ''}
                  </Title>
                </Box>

                {plansLoading ? (
                  <Box display="flex" gap="4" justifyContent="center">
                    <Card>
                      <Card.Body>
                        <Box display="grid" gap="2">
                          <Text.Skeleton width="80%" />
                          <Text.Skeleton width="60%" />
                          <Text.Skeleton width="100%" />
                        </Box>
                      </Card.Body>
                    </Card>
                    <Card>
                      <Card.Body>
                        <Box display="grid" gap="2">
                          <Text.Skeleton width="80%" />
                          <Text.Skeleton width="60%" />
                          <Text.Skeleton width="100%" />
                        </Box>
                      </Card.Body>
                    </Card>
                  </Box>
                ) : plans?.prices && plans.prices.length > 0 ? (
                  <PlanDisplay minWidth={{ xs: '150px', md: '300px' }} mx="4">
                    {plans.prices.map((price: StripePrice) => {
                      const intervalText =
                        price.interval === 'month'
                          ? t('BillingInfo.Monthly') || ''
                          : t('BillingInfo.Yearly') || '';

                      const planName = price.nickname || intervalText;
                      const perIntervalText =
                        price.interval === 'month'
                          ? t('BillingInfo.PerMonth') || ''
                          : t('BillingInfo.PerYear') || '';

                      // 📊 Calcular desconto para plano anual
                      const getDiscountPercentage = () => {
                        // if (price.interval !== 'year') return null;

                        // const monthlyPrice = plans.prices.find(
                        //   (p: StripePrice) => p.interval === 'month',
                        // );
                        // if (!monthlyPrice) return null;

                        // const monthlyYearlyTotal = monthlyPrice.amount * 12;
                        // const yearlyPrice = price.amount;
                        // const discount =
                        //   ((monthlyYearlyTotal - yearlyPrice) /
                        //     monthlyYearlyTotal) *
                        //   100;

                        return Math.round(17.5);
                      };

                      const discountPercentage = getDiscountPercentage();

                      return (
                        <PlanDisplay.Card
                          key={price.stripe_price_id}
                          highlighted={price.isDefault}
                        >
                          <PlanDisplay.Header
                            subtitle={t('BillingInfo.Plan') || ''}
                            title={
                              <Box display="flex" gap="1">
                                <Title as="h3" color="neutral-textHigh">
                                  {planName}
                                </Title>
                              </Box>
                            }
                          >
                            <Box display="flex" gap="1" alignItems="center">
                              <Title as="h5">
                                {formatMoney(
                                  price.amount,
                                  price.currency,
                                  undefined,
                                  { display: 'code' },
                                )}
                              </Title>
                              <Text fontWeight="bold" fontSize="highlight">
                                {perIntervalText}
                              </Text>
                              {price.interval === 'year' &&
                                discountPercentage && (
                                  <Tag appearance="success">
                                    {discountPercentage}% OFF
                                  </Tag>
                                )}
                            </Box>
                          </PlanDisplay.Header>

                          <PlanDisplay.Content>
                            {price.metadata.map((item, index) => (
                              <PlanDisplay.Bullet
                                key={index}
                                icon={<CheckIcon />}
                              >
                                {item}
                              </PlanDisplay.Bullet>
                            ))}
                          </PlanDisplay.Content>

                          <PlanDisplay.Footer>
                            <Box
                              display="flex"
                              flexDirection="column"
                              justifyContent="center"
                              alignItems="center"
                              width="100%"
                              gap="2"
                            >
                              <Input
                                name={`coupon-${price.stripe_price_id}`}
                                value={coupons[price.stripe_price_id] || ''}
                                onChange={(e) =>
                                  setCoupons((prev) => ({
                                    ...prev,
                                    [price.stripe_price_id]: e.target.value,
                                  }))
                                }
                                placeholder={
                                  t('BillingInfoComponent.CouponPlaceholder') ||
                                  ''
                                }
                              />

                              <SubmitButton
                                loading={ctaLoading}
                                loadingText={
                                  t('BillingInfoComponent.Start') || ''
                                }
                                appearance="primary"
                                onClick={() =>
                                  createCheckoutSession(
                                    price.stripe_price_id,
                                    coupons[price.stripe_price_id],
                                  )
                                }
                              >
                                <Text fontSize="base" color="currentColor">
                                  {t('BillingInfoComponent.Start')}
                                </Text>
                              </SubmitButton>
                            </Box>
                          </PlanDisplay.Footer>
                        </PlanDisplay.Card>
                      );
                    })}
                  </PlanDisplay>
                ) : (
                  <Text textAlign="center" color="neutral-textLow">
                    {t('BillingInfo.NoPlansAvailable')}
                  </Text>
                )}
              </Box>
            </Box>
          </Card.Body>
        </Card>
      )}
    </Box>
  );
};

export default BillingInfo;
